/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as constants from '../../constants';

dayjs.extend(utc);

/**
 * A past-date range picked in a scheduler calendar. `anchor` is the first past date clicked and
 * stays fixed; `other` is the optional second click, on either side of it.
 * @typedef {{ anchor: import('dayjs').Dayjs|null, other: import('dayjs').Dayjs|null }} EvalCustomRange
 */

export const EMPTY_CUSTOM_RANGE = Object.freeze({ anchor: null, other: null });

export const CUSTOM_EVAL_NOTICE_KEYS = Object.freeze({
  DAILY: 'customEval.NOTICE_DAILY',
  ONE_TIME: 'customEval.NOTICE_ONE_TIME',
  NONE: 'customEval.NOTICE_NO_SCHEDULE'
});

const isValidDay = (value) => !!value?.isValid?.();

const localToday = () => dayjs().startOf(constants.DATE_UNIT_DAY);

export const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';

/**
 * @typedef {{ maxSpanDays: number }} CustomEvalLimits
 */

/** A positive whole number no larger than the cap, otherwise the default. */
const resolveLimit = (value, fallback, cap) => (Number.isInteger(value) && value > 0 ? Math.min(value, cap) : fallback);

/**
 * The tenant's custom eval limits from its `customEval` config, falling back to the default when
 * missing or invalid and never exceeding the cap. There is no limit on how far back a range may
 * start. Mirrors the backend's validation.
 * @param {object|null|undefined} tenant
 * @returns {CustomEvalLimits}
 */
export const resolveCustomEvalLimits = (tenant) => {
  const config = tenant?.[constants.TENANT_CUSTOM_EVAL_KEY];
  return {
    maxSpanDays: resolveLimit(
      config?.maxSpanDays,
      constants.EVAL_CUSTOM_RANGE_DEFAULT_SPAN_DAYS,
      constants.EVAL_CUSTOM_RANGE_SPAN_DAYS_CAP
    )
  };
};

export const DEFAULT_CUSTOM_EVAL_LIMITS = Object.freeze(resolveCustomEvalLimits(null));

export const isPastLocalDate = (date) => isValidDay(date) && date.isBefore(localToday(), constants.DATE_UNIT_DAY);

/** @param {EvalCustomRange|null|undefined} range */
export const hasCustomRange = (range) => isValidDay(range?.anchor);

/**
 * Days selectable as the second end once an anchor is fixed: up to the span either side of it,
 * never after today.
 * @param {import('dayjs').Dayjs} anchor
 * @param {CustomEvalLimits} limits
 * @returns {{ min: import('dayjs').Dayjs, max: import('dayjs').Dayjs }}
 */
export const resolveCustomRangeWindow = (anchor, limits) => {
  const reach = limits.maxSpanDays - 1;
  const today = localToday();
  const latest = anchor.add(reach, constants.DATE_UNIT_DAY);
  return {
    min: anchor.subtract(reach, constants.DATE_UNIT_DAY),
    max: latest.isAfter(today, constants.DATE_UNIT_DAY) ? today : latest
  };
};

/**
 * Only narrows the calendar once an anchor exists; before that the calendar's own `minDate`
 * applies so future dates stay open for normal scheduling.
 * @param {import('dayjs').Dayjs} date
 * @param {EvalCustomRange|null|undefined} range
 * @param {CustomEvalLimits} limits
 * @returns {boolean}
 */
export const isCustomRangeDateDisabled = (date, range, limits) => {
  if (!hasCustomRange(range)) {
    return false;
  }
  const { min, max } = resolveCustomRangeWindow(range.anchor, limits);
  return date.isBefore(min, constants.DATE_UNIT_DAY) || date.isAfter(max, constants.DATE_UNIT_DAY);
};

/**
 * Next range state for a calendar click: the first past date becomes the anchor, clicking the
 * anchor again clears the range, and any other day inside the window becomes the second end.
 * @param {EvalCustomRange|null|undefined} range
 * @param {import('dayjs').Dayjs} date
 * @param {CustomEvalLimits} limits
 * @returns {EvalCustomRange}
 */
export const applyCustomRangeClick = (range, date, limits) => {
  const day = date.startOf(constants.DATE_UNIT_DAY);
  if (!hasCustomRange(range)) {
    return isPastLocalDate(day) ? { anchor: day, other: null } : EMPTY_CUSTOM_RANGE;
  }
  if (day.isSame(range.anchor, constants.DATE_UNIT_DAY)) {
    return EMPTY_CUSTOM_RANGE;
  }
  return isCustomRangeDateDisabled(day, range, limits) ? range : { anchor: range.anchor, other: day };
};

/**
 * Chronological ends of a range; a lone anchor is a single-day range.
 * @param {EvalCustomRange|null|undefined} range
 * @returns {{ start: import('dayjs').Dayjs, end: import('dayjs').Dayjs }|null}
 */
export const orderCustomRange = (range) => {
  if (!hasCustomRange(range)) {
    return null;
  }
  const other = isValidDay(range.other) ? range.other : range.anchor;
  return other.isBefore(range.anchor, constants.DATE_UNIT_DAY)
    ? { start: other, end: range.anchor }
    : { start: range.anchor, end: other };
};

/**
 * How a calendar day relates to the range, for highlighting.
 * @param {import('dayjs').Dayjs} day
 * @param {{ start: import('dayjs').Dayjs, end: import('dayjs').Dayjs }|null} bounds
 * @param {import('dayjs').Dayjs|null} anchor
 * @returns {string|null} One of constants.EVAL_CUSTOM_RANGE_DAY_ROLE, or null when outside the range
 */
export const resolveCustomRangeDayRole = (day, bounds, anchor) => {
  if (!bounds || !isValidDay(day)) {
    return null;
  }
  if (day.isSame(anchor, constants.DATE_UNIT_DAY)) {
    return constants.EVAL_CUSTOM_RANGE_DAY_ROLE.ANCHOR;
  }
  if (day.isSame(bounds.start, constants.DATE_UNIT_DAY) || day.isSame(bounds.end, constants.DATE_UNIT_DAY)) {
    return constants.EVAL_CUSTOM_RANGE_DAY_ROLE.EDGE;
  }
  return day.isAfter(bounds.start, constants.DATE_UNIT_DAY) && day.isBefore(bounds.end, constants.DATE_UNIT_DAY)
    ? constants.EVAL_CUSTOM_RANGE_DAY_ROLE.INSIDE
    : null;
};

/**
 * The range as short as it can be without losing information. The default dash style gives "9 Sep 2026",
 * "9–13 Sep 2026", "28 Aug – 3 Sep 2026"; the arrow style gives "9 Sep → 13 Sep 2026". Both dates are in
 * full when the years differ.
 * @param {EvalCustomRange|null|undefined} range
 * @param {{ sameMonthStartFormat: string, sameMonthSeparator: string, separator: string }} [style]
 */
export const formatCustomRangeLabel = (range, style = constants.EVAL_CUSTOM_RANGE_DASH_STYLE) => {
  const bounds = orderCustomRange(range);
  if (!bounds) {
    return '';
  }
  const { start, end } = bounds;
  const endLabel = end.format(constants.EVAL_CUSTOM_RANGE_DISPLAY_FORMAT);
  if (start.isSame(end, constants.DATE_UNIT_DAY)) {
    return endLabel;
  }
  if (start.isSame(end, constants.DATE_UNIT_MONTH)) {
    return `${start.format(style.sameMonthStartFormat)}${style.sameMonthSeparator}${endLabel}`;
  }
  const startFormat = start.isSame(end, constants.DATE_UNIT_YEAR)
    ? constants.EVAL_CUSTOM_RANGE_DAY_MONTH_FORMAT
    : constants.EVAL_CUSTOM_RANGE_DISPLAY_FORMAT;
  return `${start.format(startFormat)}${style.separator}${endLabel}`;
};

/**
 * Notice shown while a past range is selected, worded for the schedule that stays in place.
 * @param {{ scheduledAt: import('dayjs').Dayjs|null, recurring: boolean }} params
 * @returns {string} i18n key
 */
export const resolveCustomEvalNoticeKey = ({ scheduledAt, recurring }) => {
  if (!isValidDay(scheduledAt)) {
    return CUSTOM_EVAL_NOTICE_KEYS.NONE;
  }
  if (recurring) {
    return CUSTOM_EVAL_NOTICE_KEYS.DAILY;
  }
  return scheduledAt.isAfter(dayjs()) ? CUSTOM_EVAL_NOTICE_KEYS.ONE_TIME : CUSTOM_EVAL_NOTICE_KEYS.NONE;
};

/**
 * Body for POST /rubrics/:rubricId/custom-eval.
 * @param {{ range: EvalCustomRange, evalSamplePercent: number|string, isConversational: boolean }} params
 * @returns {{ startDate: string, endDate: string, timeZone: string, evalSamplePercent: number, isConversational: boolean }}
 */
export const buildCustomEvalRequestBody = ({ range, evalSamplePercent, isConversational }) => {
  const { start, end } = orderCustomRange(range);
  return {
    startDate: start.format(constants.EVAL_CUSTOM_RANGE_DATE_FORMAT),
    endDate: end.format(constants.EVAL_CUSTOM_RANGE_DATE_FORMAT),
    timeZone: getBrowserTimeZone(),
    evalSamplePercent: Number(evalSamplePercent),
    isConversational: !!isConversational
  };
};

/**
 * Keeps only the custom eval fields list screens show; a record without both range dates is treated as absent.
 * @param {object} customEval raw `customEval` from the rubric list API
 * @returns {{ startDate: string, endDate: string, evalSamplePercent: number|string, isConversational: boolean, requestedBy: string, requestedDate: string|null }|null}
 */
export const normalizeCustomEvalSummary = (customEval) => {
  if (!customEval?.startDate || !customEval?.endDate) {
    return null;
  }
  return {
    startDate: String(customEval.startDate),
    endDate: String(customEval.endDate),
    evalSamplePercent: customEval.evalSamplePercent ?? '',
    isConversational: customEval.isConversational === true,
    requestedBy: String(customEval.requestedBy ?? '').trim(),
    requestedDate: customEval.requestedDate ?? null
  };
};

/**
 * Display values for a custom eval summary. The range dates are the requester's calendar days, so they are
 * shown as stored; requestedDate is a UTC timestamp and is shown in the viewer's local time.
 * @param {ReturnType<typeof normalizeCustomEvalSummary>} customEval
 * @param {string} [requestedFormat]
 * @returns {{ rangeLabel: string, dayCount: number, samplePercent: string, isConversational: boolean, requestedAt: string, requestedBy: string }|null}
 */
export const resolveCustomEvalDetails = (customEval, requestedFormat = constants.EVAL_CUSTOM_EVAL_REQUESTED_FORMAT) => {
  const start = customEval ? dayjs(customEval.startDate) : null;
  const end = customEval ? dayjs(customEval.endDate) : null;
  if (!isValidDay(start) || !isValidDay(end)) {
    return null;
  }
  const requested = customEval.requestedDate ? dayjs.utc(customEval.requestedDate).local() : null;
  const samplePercent = customEval.evalSamplePercent;
  return {
    rangeLabel: formatCustomRangeLabel({ anchor: start, other: end }, constants.EVAL_CUSTOM_RANGE_ARROW_STYLE),
    dayCount: Math.abs(end.diff(start, constants.DATE_UNIT_DAY)) + 1,
    samplePercent: samplePercent === '' || samplePercent == null ? '' : String(samplePercent),
    isConversational: customEval.isConversational,
    requestedAt: isValidDay(requested) ? requested.format(requestedFormat) : '',
    requestedBy: customEval.requestedBy
  };
};
